import { InputTextarea } from "primereact/inputtextarea";
import { useEffect, useRef, useState } from "react";
import { Control, Controller, FieldPath, FieldValues, Path, RegisterOptions } from "react-hook-form";
import { cn } from "../ui/cn";
import { getClosestBgClass } from "@/common/common_utils";
import { TextBodyExtraExtraSmall } from "../text/Texts";
import { Column, Row } from "../container/Container";

interface ResizeTextAreaProps<T extends FieldValues> {
	id: FieldPath<T>;
	control: Control<T>;
	label: string;
	formName: string;
	containerClassName?: string;
	rules?: RegisterOptions<T, Path<T>>;

	maxLength?: number;
	showCharCount?: boolean;
	floatLabelOnFocus?: boolean;

	onRowsChange?: (rows: number) => void;
	oneRowHeightValue?: string;
	multiRowHeightValue?: (rows: number) => string;
	oneRowYPaddingClass?: string;
	multiRowYPaddingClass?: string;
};

const ResizeTextArea = <T extends FieldValues>({
	id,
	control,
	label,
	formName,
	containerClassName,
	rules,

	maxLength,
	showCharCount = false,
	floatLabelOnFocus = false,

	onRowsChange,
	oneRowHeightValue = `${28 * 1 + 14 * 2}px`,
	multiRowHeightValue = (rows: number) => `${28 * rows + 8 * 2}px`,
	oneRowYPaddingClass = 'py-3.5',
	multiRowYPaddingClass = 'py-2',
}: ResizeTextAreaProps<T>) => {
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const [isFocus, setIsFocus] = useState(false);

	const [heightValue, setHeightValue] = useState(oneRowHeightValue);
	const [yPaddingClass, setYPaddingClass] = useState(oneRowYPaddingClass);
	const [bgLabelClass, setBgLabelClass] = useState<string>('bg-transparent');

	const calculateRows = (textarea: HTMLTextAreaElement) => {
		const lineHeightStr = getComputedStyle(textarea).lineHeight
		const lineHeight = lineHeightStr.endsWith('px') ? parseFloat(lineHeightStr) : 16;

		const rows = textarea.scrollHeight / lineHeight;
		if (!Number.isInteger(rows)) throw Error('ResizeTextArea calculated rows shouldnt be float');
		return rows;
	}

	const calculateHeight = (value: string) => {
		if (!textareaRef.current) return;
		const textareaWidth = getComputedStyle(textareaRef.current).width;

		const virtTextarea = document.createElement('textarea');
		virtTextarea.className = cn(`font-poppins text-body-large text-onSurface w-[${textareaWidth}] h-auto px-4 overflow-hidden`);
		virtTextarea.value = value;
		virtTextarea.rows = 1;
		document.body.appendChild(virtTextarea);

		const rows = calculateRows(virtTextarea);
		let newHeightValue: string;
		let newYPaddingClass: string;
		if (rows == 1) {
			newHeightValue = oneRowHeightValue;
			newYPaddingClass = oneRowYPaddingClass;
		} else {
			newHeightValue = multiRowHeightValue(rows);
			newYPaddingClass = multiRowYPaddingClass;
		}

		if (heightValue != newHeightValue) onRowsChange?.(rows);
		setHeightValue(newHeightValue);
		setYPaddingClass(newYPaddingClass);
		document.body.removeChild(virtTextarea);
	}

	useEffect(() => {
		if (textareaRef.current) {
			setBgLabelClass(getClosestBgClass(textareaRef.current));
		};
	}, []);

	return (
		<Controller
			name={id}
			control={control}
			rules={rules}
			render={({ field, fieldState: { error } }) => {
				const hasValue = !!field.value;
				const charCount = field.value?.length || 0;

				useEffect(() => calculateHeight(field.value), [field.value]);

				return (
					<Column className={cn('relative w-full', containerClassName)}>
						<InputTextarea
							{...field}
							unstyled
							id={formName + id}
							ref={textareaRef}
							maxLength={maxLength}
							rows={1}
							onFocus={() => setIsFocus(true)} onBlur={() => setIsFocus(false)}
							value={field.value}
							className={cn(
								yPaddingClass,
								'font-poppins text-body-large text-onSurface',
								'w-full px-4 bg-transparent rounded-[4px]',
								'outline-none resize-none overflow-hidden',
								'transition-all duration-200', isFocus
									? error ? 'shadow-[inset_0_0_0_3px_theme(colors.error60)]' : 'shadow-[inset_0_0_0_3px_theme(colors.primary60)]'
									: error ? 'shadow-[inset_0_0_0_1px_theme(colors.error60)]' : 'shadow-[inset_0_0_0_1px_theme(colors.primary60)]',
							)}
							style={{ height: heightValue }}
						/>
						<label
							htmlFor={formName + id}
							children={label}
							className={cn(
								'absolute z-10 origin-[0] px-1 left-3',
								'transition-all duration-300',
								'font-poppins text-body-large',
								error ? 'text-error60' : isFocus ? 'text-primary60' : 'text-neutralVariant20',
								hasValue && 'leading-[18px]',
								hasValue || (floatLabelOnFocus && isFocus)
									? `-translate-y-1/2 scale-[78%] ${bgLabelClass}`
									: 'translate-y-1/2 scale-100 bg-transparent',
							)}
						/>

						<Row className="mt-1 mx-4 justify-between">
							<TextBodyExtraExtraSmall
								id={`${formName}-helper`}
								className="self-start block text-error"
								text={error ? error?.message ?? '' : ''}
							/>
							<TextBodyExtraExtraSmall
								id={`${formName}-count`}
								className="self-end block text-onSurfaceVariant"
								text={showCharCount && maxLength ? `${charCount} of ${maxLength} characters` : ''}
							/>
						</Row>
					</Column>
				);
			}}
		/>
	);
}

export default ResizeTextArea;
